import type { RumEvent, RumInitConfiguration } from '@datadog/browser-rum'

// scrub the really long sso token because it's unhelpful and noisy in events
const SSO_PATH_PATTERN = /(\/(?:md\/[^/?#]+|login)\/)[^?#]+/g

export const redactSsoPath = <T extends string | undefined>(value: T): T =>
  typeof value === 'string' ? (value.replace(SSO_PATH_PATTERN, '$1REDACTED') as T) : value

export interface RumBeforeSendOptions {
  scrubSsoPaths?: boolean
}

// API calls and the page's own navigation timing are always kept; only successful static assets are dropped
const KEPT_RESOURCE_TYPES = new Set(['xhr', 'fetch', 'document'])

// runs on every RUM event before it is sent to Datadog
export function buildRumBeforeSend({
  scrubSsoPaths = false,
}: RumBeforeSendOptions = {}): NonNullable<RumInitConfiguration['beforeSend']> {
  return (event: RumEvent) => {
    if (scrubSsoPaths) {
      event.view.url = redactSsoPath(event.view.url)
      event.view.referrer = redactSsoPath(event.view.referrer)
      if (event.type === 'resource') event.resource.url = redactSsoPath(event.resource.url)
      if (event.type === 'error' && event.error.resource) {
        event.error.resource.url = redactSsoPath(event.error.resource.url)
      }
    }

    if (event.type !== 'resource') return true
    if (KEPT_RESOURCE_TYPES.has(event.resource.type)) return true
    const status = event.resource.status_code
    return !(status !== undefined && status > 0 && status < 400)
  }
}

//  Holds errors that happen before Datadog RUM has started up, so they aren't lost
export interface RumErrorBuffer<Context = Record<string, unknown>> {
  push(error: unknown, context?: Context): boolean
  flush(send: (error: unknown, context?: Context) => void): void
  readonly size: number
}

export const DEFAULT_RUM_ERROR_BUFFER_SIZE = 50

export function createRumErrorBuffer<Context = Record<string, unknown>>(
  maxSize: number = DEFAULT_RUM_ERROR_BUFFER_SIZE,
): RumErrorBuffer<Context> {
  const items: Array<{ error: unknown; context?: Context }> = []
  return {
    push(error, context) {
      if (items.length >= maxSize) return false
      items.push({ error, context })
      return true
    },
    flush(send) {
      while (items.length > 0) {
        const item = items.shift()
        if (item) send(item.error, item.context)
      }
    },
    get size() {
      return items.length
    },
  }
}
